import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Theme,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CloseIcon from "@mui/icons-material/Close";
import { selectedStatesAlpha } from "../refdata/FilterOptions";

interface Props {
  children: React.ReactNode;
  title: string;
  display: boolean;
  setDisplay: (display: boolean) => void;
  subtitle?: React.ReactNode;
  onExited?: () => void;
  maxWidth?: DialogProps["maxWidth"];
  id?: string;
  onAction?: () => void;
  onSubmit?: () => void;
  submitText?: string;
  disabledSubmit?: boolean;
  partialMergerModal?: any;
  noCloseXIcon?: boolean;
  selectedLocations?: any;
  moveActionButtons?: boolean;
  overwrite?: string;
  handleClick?: () => void;
}

const useClasses = makeStyles((theme: Theme) => ({
  root: {
    "& #modal-title-box": {
      paddingBottom: "0px",
      marginBottom: "-15px",
    },
    "& .MuiDialogTitle-root": {
      "& h2": {
        "fontSize": 16,
        "padding-bottom": 0,
      },
      "& #modal-title": {
        "padding-bottom": 2,
      },
      "& #modal-content > *": {
        padding: 0,
      },
    },
  },
  dialogActionsTop: {
    "& button.MuiButton-containedSecondary": {
      borderRadius: 3,
      width: 200,
      fontSize: "1rem",
      marginRight: "1rem",
    },
    "& button.MuiButton-contained.Mui-disabled": {
      backgroundColor: "#e0e0e0",
    },
  },
  dialogActions: {
    "marginTop": "1rem",
    "backgroundColor": theme.palette.primary.main,
    "& button.MuiButton-containedSecondary": {
      borderRadius: 3,
      width: 200,
      fontSize: "1rem",
      height: 40,
      margin: "1rem",
    },
    "& button.MuiButton-contained.Mui-disabled": {
      backgroundColor: "#e0e0e0",
    },
  },
  closeButton: {
    "position": "absolute",
    "right": theme.spacing(1),
    "top": theme.spacing(1),
    "color": "#acc8df",
    "padding": 0,
    "marginRight": 16,
    "background-color": "transparent",
    "& svg": {
      fontSize: "2rem",
    },
    "&:focus": {
      background: "rgba(0, 0, 0, 0.04)",
    },
  },
  selectedLocationStyle: {
    fontSize: 18,
    color: theme.palette.primary.main,
    fontWeight: "bold",
    padding: 1,
  },
  divider: {
    "&.MuiDivider-root": {
      margin: "15px auto 15px auto",
      borderColor: "#e4eef7",
      padding: "0px",
    },
  },
  modalContent: {
    "&#modal-content": {
      overflowX: "hidden",
      padding: "24px 24px 8px 24px",
    },
  },
  cancelButton: {
    "backgroundColor": "white",
    "&:hover, &.Mui-focusVisible": {
      "color": "#ffffff",
      "&#closeModal": { backgroundColor: theme.palette.primary.main },
    },
  },
  saveButton: {
    "&:hover, &.Mui-focusVisible": {
      "color": "#ffffff",
      "&#submitModal": { backgroundColor: theme.palette.primary.main },
    },
  },
}));

export default function ModalDialog({
  title,
  subtitle = <></>,
  children,
  display,
  setDisplay,
  onExited,
  maxWidth,
  id,
  onAction,
  onSubmit,
  submitText,
  disabledSubmit,
  partialMergerModal,
  noCloseXIcon,
  selectedLocations,
  moveActionButtons,
  overwrite,
  handleClick,
}: Props) {
  const classes = useClasses();

  const handleClose = () => setDisplay(false);
  const dialogActions = (
    <DialogActions
      className={
        moveActionButtons ? classes.dialogActionsTop : classes.dialogActions
      }
    >
      <div>
        <Button
          className={moveActionButtons ? classes.cancelButton : ""}
          variant="contained"
          color="secondary"
          onClick={handleClick || handleClose}
          id="closeModal"
          style={
            moveActionButtons ? { marginBottom: "0", marginRight: "16px" } : {}
          }
        >
          {overwrite || "Cancel"}
        </Button>
        <Button
          className={moveActionButtons ? classes.saveButton : ""}
          disabled={disabledSubmit}
          variant="contained"
          color="secondary"
          type={onSubmit ? "submit" : "button"}
          id="submitModal"
          onClick={onAction}
          style={moveActionButtons ? { marginBottom: "0" } : {}}
        >
          {submitText ? submitText : "Done"}
        </Button>
      </div>
    </DialogActions>
  );

  const dialogBody = (
    <>
      <DialogTitle id="modal-title-box">
        <Grid container>
          <Grid item xs={6} style={{ textAlign: "left" }}>
            <Typography
              id="modal-title"
              color="primary"
              variant="h5"
              component="div"
            >
              <b>{title}</b>
            </Typography>
          </Grid>
          <Grid item xs={6} style={{ textAlign: "left" }}>
            {selectedLocations ? (
              <>
                <Typography
                  id="selected-locations"
                  className={classes.selectedLocationStyle}
                >
                  Selected Locations{" "}
                </Typography>
                <Typography
                  component="span"
                  style={{
                    color: "black",
                    fontWeight: "normal",
                    fontSize: "15px",
                    display: "inline-block",
                    whiteSpace: "normal",
                    width: "100%",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedLocations.flat().length ===
                    selectedStatesAlpha.flat().length ||
                  selectedLocations.flat().length === 0
                    ? "All States and Territories"
                    : selectedLocations.join(",")}
                </Typography>
              </>
            ) : (
              ""
            )}
          </Grid>
          {subtitle ? (
            <Grid container>
              <Grid item sm={moveActionButtons ? 4 : 12}>
                <Typography
                  id="modal-subtitle"
                  color="primary"
                  component={"div"}
                  style={
                    moveActionButtons ? { fontSize: 16, paddingTop: 14 } : {}
                  }
                >
                  {subtitle}
                </Typography>
              </Grid>
              <Grid item sm={8}>
                {moveActionButtons && dialogActions}
              </Grid>
            </Grid>
          ) : (
            ""
          )}
        </Grid>

        {!noCloseXIcon && (
          <IconButton
            id="close-modal-button"
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
            size="large"
          >
            {!partialMergerModal && <CloseIcon />}
          </IconButton>
        )}
        <Divider className={classes.divider} />
      </DialogTitle>
      <DialogContent
        id="modal-content"
        className={classes.modalContent}
        style={partialMergerModal ? { paddingBottom: 0 } : {}}
      >
        {children}
      </DialogContent>
    </>
  );

  return (
    <Dialog
      id={id ? id : "modal"}
      open={display}
      disableAutoFocus={false}
      onClose={(event, reason) => {
        if (reason === "backdropClick" && !noCloseXIcon) {
          handleClose();
        }
      }}
      maxWidth={maxWidth ? maxWidth : "md"}
      fullWidth
      className={classes.root}
      aria-describedby="modal-title"
      aria-labelledby="modal-title"
      TransitionProps={{ onExited: onExited }}
    >
      {onSubmit ? (
        <form
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            onSubmit();
          }}
          id="modal-dialog"
        >
          {dialogBody}
          {!moveActionButtons && dialogActions}
        </form>
      ) : (
        <>
          {dialogBody}
          {onAction && dialogActions}
        </>
      )}
    </Dialog>
  );
}
